import { useLayoutEffect, useRef, useState } from "react";

import {
  areLayoutsEqual,
  computeSpacePathLayout,
  createCanvasMeasurer,
  SpacePathLayout,
} from "./helpers";
import { scheduleSpacePathAvailableWidthMeasure } from "./measureAvailableWidth";

const fullLayout = (names: string[]): SpacePathLayout => ({
  leadingEllipsis: false,
  segments: names.length > 0 ? names : [""],
  ellipsizeLast: false,
  truncated: false,
});

/**
 * Measures the width available to the path container and resolves how the given
 * segment `names` should be truncated to fit. Recomputes on container resize.
 *
 * Falls back to rendering the full path when width/font can't be measured (e.g. in
 * jsdom), so the truncation itself is unit-tested against `computeSpacePathLayout`.
 */
const useSpacePathLayout = (names: string[], enabled = true) => {
  const ref = useRef<HTMLSpanElement>(null);

  // Serialized so the effect can depend on a stable primitive instead of a new array.
  const namesKey = JSON.stringify(names);
  const [layout, setLayout] = useState<SpacePathLayout>(() => fullLayout(names));

  useLayoutEffect(() => {
    const currentNames = JSON.parse(namesKey) as string[];

    if (!enabled) {
      return;
    }

    const node = ref.current;

    if (!node) {
      setLayout(fullLayout(currentNames));
      return;
    }

    const initialMeasure = createCanvasMeasurer(node);
    if (!initialMeasure) {
      setLayout(fullLayout(currentNames));
      return;
    }

    let textMeasure = initialMeasure;
    let cancelScheduledMeasure: (() => void) | undefined;

    const update = () => {
      cancelScheduledMeasure = scheduleSpacePathAvailableWidthMeasure(node, (width) => {
        const next = computeSpacePathLayout(currentNames, width, textMeasure);
        // The measure is flushed synchronously, so bailing out on an unchanged layout keeps
        // a column drag from re-rendering every path in the table on every observer tick.
        setLayout((current) => (areLayoutsEqual(current, next) ? current : next));
      });
    };

    update();

    // Canvas measureText uses fallback metrics until document fonts have loaded.
    let cancelled = false;
    const fontsReady = document.fonts?.ready;
    if (fontsReady) {
      fontsReady.then(() => {
        if (cancelled) {
          return;
        }

        const nextMeasure = createCanvasMeasurer(node);
        if (nextMeasure) {
          textMeasure = nextMeasure;
        }

        update();
      });
    }

    // Observe the SpacePath root's parent: its size (not the path's own collapsible
    // width) is what determines the room available to us.
    const spacePathRoot = node.closest("[data-space-path]");
    const observed = spacePathRoot?.parentElement ?? node.parentElement?.parentElement ?? node;
    const observer = new ResizeObserver(update);
    observer.observe(observed);

    return () => {
      cancelled = true;
      cancelScheduledMeasure?.();
      observer.disconnect();
    };
  }, [namesKey, enabled]);

  return { ref, layout };
};

export default useSpacePathLayout;
